<template>
  <div class="ai-report-detail-container pb-3">
    <!-- 加载状态 -->
    <div v-if="loading" class="text-center py-5 my-5">
      <b-spinner small variant="primary"></b-spinner>
    </div>

    <!-- 极致紧凑详情卡片 -->
    <div v-else-if="report" class="detail-card shadow-sm rounded-lg overflow-hidden border" :class="{ 'chart-blurred': requireSubscription }">
      <!-- 紧凑头部 -->
      <div class="detail-header px-3 py-1 text-white">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center overflow-hidden flex-grow-1">
            <b-icon icon="arrow-left" class="cursor-pointer mr-2 back-icon" @click="$router.go(-1)"></b-icon>
            <div class="d-flex flex-column flex-md-row align-items-md-center overflow-hidden">
              <span class="font-weight-bold mr-md-2 text-truncate report-title-text">{{ typeName }}【{{ report.created_time ? report.created_time.split(' ')[0] : '' }}】</span>
              <div class="d-flex align-items-center opacity-8 x-small mt-0 info-bar flex-wrap">
                <span class="mr-2 d-none d-sm-inline"><b-icon icon="person" class="mr-1"></b-icon>{{ report.username || 'AI' }}</span>
                <span class="mr-2"><b-icon icon="clock" class="mr-1"></b-icon>{{ report.created_time }}</span>
                <span class="cursor-pointer ml-1 like-indicator" @click="toggleLike">
                  <img :src="isLiked ? likedIcon : likeIcon" class="mr-1 thumb-icon-small" />
                  <span class="likes-count">{{ report.likes || 0 }}</span>
                </span>
              </div>
            </div>
          </div>
          <div class="ml-2 flex-shrink-0 d-flex align-items-center">
            <b-icon 
              v-if="!requireSubscription && report"
              :icon="isPlaying ? (isPaused ? 'play-circle' : 'pause-circle') : 'volume-up'" 
              class="cursor-pointer mr-3" 
              font-scale="1.1"
              @click="toggleSpeech"
              :class="{'text-warning': isPlaying && !isPaused}"
              title="语音朗读"
            ></b-icon>
            <b-icon 
              v-if="isPlaying && !requireSubscription && report" 
              icon="stop-circle" 
              class="cursor-pointer mr-3 text-danger" 
              font-scale="1.1" 
              @click="stopSpeech" 
              title="停止朗读"
            ></b-icon>
            <b-icon icon="share" class="cursor-pointer share-icon" font-scale="1.1" @click="shareReport"></b-icon>
          </div>
        </div>
      </div>

      <!-- 紧凑正文 -->
      <div class="detail-body px-3 py-2">
        <article class="report-content" v-html="formattedContent" @click="handleContentClick"></article>

        <!-- 紧凑图片 -->
        <div v-if="images.length" class="report-images mt-2 pt-1 border-top">
          <div v-for="(img, idx) in images" :key="idx" class="mb-1 text-center">
            <img :src="img" class="img-fluid rounded border" style="max-height: 600px;" />
          </div>
        </div>
        
        <!-- 文章末尾声明 -->
        <div class="mt-3 py-2 border-top text-center">
          <span class="text-muted" style="font-size: 0.8rem; opacity: 0.8;">声明：AI生成内容, 仅作为学习交流之用, 请勿用于投资。</span>
        </div>
        
        <!-- 底部交互 - 极致紧凑 -->
        <div class="mt-2 pt-1 border-top d-flex justify-content-center align-items-center">
          <b-button :variant="isLiked ? 'primary' : 'outline-primary'" size="sm" class="rounded-pill px-3 py-0 mr-3" style="height: 20px; font-size: 0.7rem;" @click="toggleLike">
            <b-icon :icon="isLiked ? 'hand-thumbs-up-fill' : 'hand-thumbs-up'" class="mr-1" font-scale="0.8"></b-icon>
            {{ isLiked ? '已赞' : '有用' }}
          </b-button>
          <WxShare :title="shareTitle" :desc="shareDesc" :link="shareLink" />
        </div>
      </div>
    </div>

    <div v-else class="text-center py-5">
      <p class="text-muted">未找到报告</p>
    </div>

    <!-- Subscription Overlay -->
    <div v-if="requireSubscription" class="subscription-overlay">
      <div class="overlay-content">
        <b-icon icon="lock" font-scale="3" class="mb-3 lock-icon"></b-icon>
        <h3>该功能属于高级付费模块</h3>
        <p>解锁【{{ typeName }}】模块，获取每日{{ isEvening ? '深度复盘、核心主线及题材异动追踪' : '早盘核心主线、龙头个股及详细催化逻辑' }}。</p>
        <b-button variant="primary" size="lg" @click="$router.push({ path: '/subscription', query: { module: subscriptionModule }})">前往解锁</b-button>
      </div>
    </div>

    <!-- 股票K线侧边栏 -->
    <StockInfoSidebar :symbol_code="sidebarSymbolCode" :symbol_name="sidebarSymbolName" :date_mark="sidebarDateMark" />

    <!-- 改进版的分享弹窗 -->
    <b-modal v-model="showShareModal" title="分享报告" centered hide-footer @shown="generateShareQr" body-class="p-0">
      <div class="share-modal-content p-3">
        <!-- 裂变推广提示文案 -->
        <div class="referral-alert text-center mb-3 p-2 rounded">
          <p class="text-primary font-weight-bold mb-1" style="font-size: 0.9rem; line-height: 1.4;">
            <b-icon icon="gift-fill" class="mr-1"></b-icon>邀请好友注册，您将获得分享积分奖励（{{ rewardPoints }}积分）
          </p>
          <p v-if="!hasInviteCode" class="text-danger x-small mb-0">
            <b-icon icon="exclamation-circle-fill" class="mr-1"></b-icon>注意：您当前未登录，分享后可能无法获得积分奖励
          </p>
        </div>

        <!-- 微信环境特有提示 -->
        <div v-if="inWeChat" class="wechat-guide-alert alert alert-success text-center py-2 mb-3" style="font-size: 0.85rem; line-height: 1.4;">
          <b-icon icon="info-circle-fill" class="mr-1"></b-icon>
          已配置微信分享卡片！请点击微信右上角 <strong class="text-success">【···】</strong> 菜单进行分享。
        </div>

        <!-- 模拟微信分享卡片预览 -->
        <div class="share-card-preview mb-3">
          <div class="share-card-left">
            <div class="share-card-title">{{ shareTitle }}</div>
            <div class="share-card-desc">{{ shareDesc }}</div>
          </div>
          <div class="share-card-right">
            <img :src="logoImg" class="share-card-thumb" alt="share-thumb">
          </div>
        </div>
        
        <!-- PC端或微信以外环境展示二维码 -->
        <div v-if="!inWeChat" class="qr-section text-center mb-3">
          <div class="qr-container bg-white p-2 rounded border mx-auto" style="width: 160px; height: 160px;">
            <canvas id="share-qr-canvas" style="width: 100%; height: 100%;"></canvas>
          </div>
          <p class="mt-2 text-muted x-small mb-0">
            {{ isMobile ? '长按二维码保存或分享给好友' : '打开微信，使用“扫一扫”即可在手机端打开分享' }}
          </p>
        </div>
        
        <!-- 链接复制与操作区 -->
        <div class="share-link-box p-2 bg-light rounded border text-left mb-3">
          <div class="small text-muted mb-1">分享链接:</div>
          <div class="text-truncate text-primary x-small">{{ shareUrl }}</div>
        </div>
        
        <div class="d-flex justify-content-between">
          <b-button v-if="isMobile && !inWeChat && hasNativeShare" variant="outline-primary" class="mr-2 flex-grow-1" @click="triggerNativeShare">
            系统分享
          </b-button>
          <b-button variant="primary" class="flex-grow-1" @click="copyShareUrl">
            复制链接
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { request_zzquant } from 'network/request.js'
import StockInfoSidebar from '@/components/stock/StockInfoSidebar'
import WxShare from '@/components/share/WxShare.vue'
import QRCode from 'qrcode'

export default {
  name: 'WonderAiReportDetail',
  components: {
    StockInfoSidebar,
    WxShare
  },
  data() {
    return {
      reportId: this.$route.params.id,
      report: null,
      loading: true,
      isLiked: false,
      likeIcon: require('assets/img/like.png'),
      likedIcon: require('assets/img/liked.png'),
      requireSubscription: false,
      // 侧边栏相关
      sidebarSymbolCode: '',
      sidebarSymbolName: '',
      sidebarDateMark: '',
      // 分享相关
      showShareModal: false,
      shareUrl: '',
      hasInviteCode: false,
      rewardPoints: 10,
      logoImg: require('assets/img/logo.png'),
      // 语音播报相关
      isPlaying: false,
      isPaused: false,
      synth: null,
      utterance: null
    }
  },
  computed: {
    formattedContent() {
      if (!this.report?.content) return ''
      let c = this.report.content
        // 1. 预处理：移除板块标题前的多余换行，确保布局紧凑
        .replace(/\n+(?=概念：|核心逻辑：|催化事件：|核心股票:|其他股票:)/g, '\n')
        .replace(/\n/g, '<br>')
        .replace(/概念：(<br>)?/g, '<span class="section-badge theme-badge">题材 <span class="badge-arrow">→</span></span><strong class="text-primary">')
        .replace(/核心逻辑：(<br>)?/g, '</strong><span class="section-badge logic-badge">逻辑 <span class="badge-arrow">→</span></span>')
        .replace(/催化事件：(<br>)?/g, '<span class="section-badge catalyst-badge">催化 <span class="badge-arrow">→</span></span>')
        .replace(/核心股票:\s*(<br>)?/g, '<span class="section-badge stock-badge">核心标的:</span>')
        .replace(/其他股票:\s*(<br>)?/g, '<span class="section-badge other-badge">其他标的:</span>')
        .replace(/\[(.*?)\]/g, (match, content) => {
          // 提取名称和代码，例如 [中际旭创(300308) +1.17%]
          const stockMatch = content.match(/^(.*?)\((\d{6})\)/)
          let dataAttrs = ''
          if (stockMatch) {
            const name = stockMatch[1]
            const code = stockMatch[2]
            dataAttrs = `data-code="${code}" data-name="${name}"`
          }
          const isUp = content.includes('+')
          const colorClass = isUp ? 'text-danger' : (content.includes('-') ? 'text-success' : 'text-primary')
          return `<span class="stock-tag ${colorClass} ${dataAttrs ? 'cursor-pointer' : ''}" ${dataAttrs}>${content}</span>`
        })
        .replace(/\*\*(.*?)\*\*/g, '<strong class="text-dark">$1</strong>')
      
      return c
    },
    images() {
      if (!this.report?.images) return []
      try {
        const imgs = typeof this.report.images === 'string' 
          ? JSON.parse(this.report.images) 
          : this.report.images
        return Array.isArray(imgs) ? imgs : [imgs]
      } catch (e) {
        return []
      }
    },
    inviteCode() {
      let code = localStorage.getItem('my_invite_code')
      if (!code) {
        try {
          const info = JSON.parse(localStorage.getItem('user_info'))
          if (info && info.invite_code) {
            code = info.invite_code
          }
        } catch (e) { /* ignore */ }
      }
      return code || ''
    },
    shareLink() {
      const origin = window.location.origin
      const path = this.$route.path
      const myCode = this.inviteCode
      
      if (myCode) {
        return `${origin}${path}?invite_code=${myCode}`
      }
      
      const urlCode = this.$route.query.invite_code
      if (urlCode) {
        return `${origin}${path}?invite_code=${urlCode}`
      }
      
      return `${origin}${path}`
    },
    isEvening() {
      if (this.report && this.report.post_type !== undefined && this.report.post_type !== null) {
        return Number(this.report.post_type) === 1
      }
      return this.$route.query.type === 'evening'
    },
    typeName() {
      return this.isEvening ? 'AI每日盘后' : 'AI每日盘前'
    },
    subscriptionModule() {
      return this.isEvening ? 'ai_postmarket' : 'ai_premarket'
    },
    shareTitle() {
      const reportDate = this.report?.created_time ? this.report.created_time.split(' ')[0] : ''
      return reportDate ? `${this.typeName}【${reportDate}】 - 自在量化` : `${this.typeName} - 自在量化`
    },
    shareDesc() {
      if (!this.report?.content) {
        return this.isEvening ? '每日盘后AI量化总结与核心题材复盘，点击查看详情。' : '每日盘前AI量化前瞻与核心题材标的预测，点击查看详情。'
      }
      const plainText = this.report.content
        .replace(/<[^>]+>/g, '') // strip HTML
        .replace(/\*\*/g, '')     // strip bold stars
        .replace(/\[|\]/g, '')    // strip brackets
        .replace(/\s+/g, ' ')     // collapse whitespace
        .trim()
      return plainText.substring(0, 100) + (plainText.length > 100 ? '...' : '')
    },
    inWeChat() {
      try {
        return /MicroMessenger/i.test(navigator.userAgent || '')
      } catch (e) {
        return false
      }
    },
    isMobile() {
      try {
        return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent || '')
      } catch (e) {
        return false
      }
    },
    hasNativeShare() {
      return !!navigator.share
    }
  },
  mounted() {
    this.loadDetail()
    this.fetchRewardConfig()
    if ('speechSynthesis' in window) {
      this.synth = window.speechSynthesis
    }
  },
  beforeDestroy() {
    this.stopSpeech()
  },
  methods: {
    fetchRewardConfig() {
      request_zzquant({
        url: '/v3/open/referral/config',
        method: 'GET'
      })
      .then(res => {
        if (res?.data?.code === 20000) {
          this.rewardPoints = res.data.data.reward_points || 10
        }
      })
      .catch(err => {
        console.warn('Fetch reward config error:', err)
      })
    },
    loadDetail() {
      this.loading = true
      request_zzquant({
        url: `/v3/ai-report/detail/${this.reportId}`,
        method: 'GET'
      })
      .then(res => {
        this.report = res?.data?.data || null
        if (this.report) {
          this.isLiked = !!this.report.liked_by_me
          // 更新浏览器标题
          const reportDate = this.report.created_time ? this.report.created_time.split(' ')[0] : ''
          if (reportDate) {
            document.title = `${this.typeName}【${reportDate}】`
          }
        }
      })
      .catch(err => {
        const status = err.status || (err.response && err.response.status)
        if (status === 403) {
          this.requireSubscription = true
          // Mock some report data for blurred preview
          this.report = {
            title: this.isEvening ? '【AI盘后】今日市场深度复盘与核心标的追踪' : '【AI盘前】今日市场主线前瞻与核心标的',
            username: 'AI',
            post_type: this.isEvening ? 1 : 0,
            created_time: new Date().toISOString().split('T')[0] + (this.isEvening ? ' 22:30:00' : ' 08:30:00'),
            content: this.isEvening
              ? '核心逻辑：今日市场复盘，主线板块延续强势...<br>催化事件：晚间重磅利好发布...<br>核心股票: [标的A(000001)] [标的B(000002)]<br>概念：数字经济<br>这是预览内容，实际内容包含非常详细的分析...'
              : '核心逻辑：今日市场预计将围绕[某主线]展开...<br>催化事件：某重磅会议召开...<br>核心股票: [标的A(000001)] [标的B(000002)]<br>概念：数字经济<br>这是预览内容，实际内容包含非常详细的分析...',
            likes: 102
          }
        } else {
          console.error('Load report detail error:', err)
        }
      })
      .finally(() => {
        this.loading = false
      })
    },
    handleContentClick(e) {
      // 通过事件委托处理 v-html 中的点击
      const target = e.target.closest('.stock-tag')
      if (target && target.dataset.code) {
        this.openStockSidebar(target.dataset.code, target.dataset.name)
      }
    },
    openStockSidebar(code, name) {
      if (!code) return
      this.sidebarSymbolCode = code
      this.sidebarSymbolName = name || ''
      // 传递报告日期作为标记，sidebar 内部会自动格式化并标注 K 线位置
      this.sidebarDateMark = this.report ? this.report.created_time : ''
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right-stock')
    },
    toggleLike() {
      if (!this.report) return
      
      request_zzquant({
        url: '/v3/ai-report/like',
        method: 'POST',
        params: {
          post_id: this.reportId
        }
      })
      .then(res => {
        const d = res?.data?.data || {}
        if (res?.data?.code === 20000) {
          this.isLiked = !!d.liked
          this.report.likes = d.likes ?? this.report.likes
          
          if (this.isLiked) {
            this.$bvToast.toast('点赞成功', { variant: 'success', solid: true, appendToast: true, toaster: 'b-toaster-top-center', autoHideDelay: 1500 })
          }
        } else {
          this.$bvToast.toast(res?.data?.message || '操作失败', { variant: 'warning', solid: true, appendToast: true, toaster: 'b-toaster-top-center' })
        }
      })
      .catch(err => {
        console.error('Toggle like error:', err)
        const msg = err.response?.data?.detail || '请先登录'
        this.$bvToast.toast(msg, { variant: 'danger', solid: true, appendToast: true, toaster: 'b-toaster-top-center' })
      })
    },
    shareReport() {
      const inviteCode = this.inviteCode
      this.hasInviteCode = !!inviteCode
      this.shareUrl = this.shareLink
      this.showShareModal = true
    },
    generateShareQr() {
      const canvas = document.getElementById('share-qr-canvas')
      if (!canvas || !this.shareUrl) return
      
      QRCode.toCanvas(canvas, this.shareUrl, {
        width: 168,
        margin: 1,
        color: {
          dark: '#333',
          light: '#fff'
        }
      }, (error) => {
        if (error) console.error('Generate QR error:', error)
      })
    },
    copyShareUrl() {
      navigator.clipboard.writeText(this.shareUrl).then(() => {
        this.$bvToast.toast('分享链接已复制', { variant: 'success', solid: true, appendToast: true, toaster: 'b-toaster-top-center', autoHideDelay: 1500 })
        this.showShareModal = false
      }).catch(() => {
        this.$bvToast.toast('复制失败，请手动复制', { variant: 'danger', solid: true })
      })
    },
    triggerNativeShare() {
      if (navigator.share) {
        navigator.share({
          title: this.shareTitle,
          text: this.shareDesc,
          url: this.shareUrl
        }).then(() => {
          this.$bvToast.toast('分享成功', { variant: 'success', solid: true, appendToast: true, toaster: 'b-toaster-top-center', autoHideDelay: 1500 })
          this.showShareModal = false
        }).catch(err => {
          console.warn('Native share failed or cancelled:', err)
        })
      }
    },
    // 语音朗读功能
    getCleanTextToRead() {
      if (!this.report?.content) return '';
      let text = this.report.content
        .replace(/<[^>]+>/g, '') // 去除HTML标签
        .replace(/\*\*/g, '') // 去除markdown粗体
        .replace(/\[|\]/g, '') // 去除中括号
        .replace(/核心逻辑：/g, '。核心逻辑：')
        .replace(/催化事件：/g, '。催化事件：')
        .replace(/核心股票:/g, '。核心股票:')
        .replace(/其他股票:/g, '。其他股票:')
        .replace(/概念：/g, '。概念：');
        
      // 处理掉过多的换行和空格，修复连续句号
      text = text.replace(/\n/g, '。').replace(/\s+/g, ' ');
      text = text.replace(/。+/g, '。');
      
      return text;
    },
    toggleSpeech() {
      if (!this.synth) {
        this.$bvToast.toast('当前浏览器不支持语音朗读', { variant: 'warning', solid: true, toaster: 'b-toaster-top-center' });
        return;
      }

      if (this.isPlaying && !this.isPaused) {
        // 正在播放，点击则暂停
        this.synth.pause();
        this.isPaused = true;
      } else if (this.isPlaying && this.isPaused) {
        // 已暂停，点击则继续
        this.synth.resume();
        this.isPaused = false;
      } else {
        // 未播放，开始播放
        this.synth.cancel(); // 先停止可能存在的其他播放
        
        const textToRead = this.getCleanTextToRead();
        if (!textToRead) return;
        
        this.utterance = new SpeechSynthesisUtterance(textToRead);
        this.utterance.lang = 'zh-CN';
        this.utterance.rate = 1.0; 
        
        this.utterance.onend = () => {
          this.isPlaying = false;
          this.isPaused = false;
        };
        
        this.utterance.onerror = (event) => {
          console.error('Speech synthesis error', event);
          this.isPlaying = false;
          this.isPaused = false;
        };
        
        this.synth.speak(this.utterance);
        this.isPlaying = true;
        this.isPaused = false;
      }
    },
    stopSpeech() {
      if (this.synth) {
        this.synth.cancel();
        this.isPlaying = false;
        this.isPaused = false;
      }
    }
  }
}
</script>

<style scoped>
.ai-report-detail-container {
  max-width: 900px;
  margin: 0 auto;
}

.detail-card {
  border-color: #eee !important;
}

.detail-header {
  background: linear-gradient(135deg, #007bff 0%, #00c6ff 100%);
  text-shadow: 0 1px 2px rgba(0,0,0,0.2);
}

.cursor-pointer {
  cursor: pointer;
  transition: opacity 0.2s;
}
.cursor-pointer:hover {
  opacity: 0.7;
}

.report-title-text {
  font-size: 1rem;
  max-width: 100%;
}

.x-small {
  font-size: 0.8rem;
  font-weight: 500;
}

.opacity-8 {
  opacity: 0.9;
}

.like-indicator {
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  padding: 0 4px;
  border-radius: 4px;
}
.like-indicator:hover {
  background: rgba(255, 255, 255, 0.2);
}
.thumb-icon-small {
  width: 14px;
  height: 14px;
  object-fit: contain;
  filter: brightness(1.5); /* 增加亮度以适应深色背景 */
}
.likes-count {
  font-weight: 600;
  margin-left: 1px;
}

.report-content {
  font-size: 1rem;
  line-height: 1.6;
  color: #333;
}

/* 内容板块样式 */
.report-content >>> .section-badge {
  display: inline-flex;
  align-items: center;
  padding: 0 8px;
  height: 22px;
  line-height: 22px;
  border-radius: 4px;
  font-size: 0.8rem;
  font-weight: 800;
  margin-right: 10px;
  vertical-align: middle;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.report-content >>> .badge-arrow {
  margin-left: 4px;
  font-family: serif;
  font-weight: bold;
}

.report-content >>> .theme-badge { background-color: #007bff; color: #fff; }
.report-content >>> .logic-badge { background-color: #20bf6b; color: #fff; }
.report-content >>> .catalyst-badge { background-color: #eb3b5a; color: #fff; }
.report-content >>> .stock-badge { background-color: #4b6584; color: #fff; }
.report-content >>> .other-badge { background-color: #778ca3; color: #fff; }

.report-content >>> .stock-tag {
  display: inline-block;
  padding: 0 4px;
  margin: 1px 2px;
  background-color: #f8f9fa;
  border-radius: 3px;
  font-weight: 600;
  font-size: 0.9rem;
}

.report-content >>> .cursor-pointer {
  cursor: pointer;
  text-decoration: underline dotted;
}

.report-content >>> .text-danger { color: #d63031 !important; }
.report-content >>> .text-success { color: #27ae60 !important; }

/* 响应式调整 */
@media (min-width: 768px) {
  .info-bar {
    border-left: 1px solid rgba(255, 255, 255, 0.4);
    padding-left: 1rem;
  }
}

.detail-body {
  background-color: #fff;
}

/* 暗黑模式适配 */
body.theme-dark .detail-header {
  background: linear-gradient(135deg, #1e3a5f 0%, #0f172a 100%);
  border-bottom: 1px solid #333;
}
body.theme-dark .detail-card {
  border-color: #333 !important;
}
body.theme-dark .detail-body {
  background-color: #1a1a1a !important;
}
body.theme-dark .report-content {
  color: #b0b3b8;
}
body.theme-dark .report-content >>> .text-primary {
  color: #58a6ff !important;
}
body.theme-dark .report-content >>> .text-dark {
  color: #f0f6fc !important;
}
body.theme-dark .report-content >>> .stock-tag {
  background-color: #21262d;
}
body.theme-dark .report-content >>> .text-danger {
  color: #ff7b72 !important;
}
body.theme-dark .report-content >>> .text-success {
  color: #3fb950 !important;
}
body.theme-dark .report-content >>> .section-badge {
  opacity: 0.85;
}
body.theme-dark .report-content >>> .theme-badge { background-color: #1f6feb; }
body.theme-dark .report-content >>> .logic-badge { background-color: #238636; }
body.theme-dark .report-content >>> .catalyst-badge { background-color: #da3633; }
body.theme-dark .report-content >>> .stock-badge { background-color: #30363d; }
body.theme-dark .report-content >>> .other-badge { background-color: #21262d; }

.subscription-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(4px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
}

body.theme-dark .subscription-overlay {
  background: rgba(30, 30, 30, 0.7);
}

.overlay-content {
  text-align: center;
  background: var(--el-bg-color, #fff);
  padding: 40px;
  border-radius: 12px;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.1);
  max-width: 400px;
}

body.theme-dark .overlay-content {
  background: #2d2d2d;
}

.lock-icon {
  color: #e6a23c;
}

.overlay-content h3 {
  margin-bottom: 12px;
  color: var(--el-text-color-primary, #303133);
}

.overlay-content p {
  color: var(--el-text-color-secondary, #909399);
  margin-bottom: 24px;
  font-size: 14px;
  line-height: 1.6;
}

.chart-blurred {
  filter: blur(8px);
  pointer-events: none;
  user-select: none;
}

/* 改进型分享弹窗样式 */
.share-modal-content {
  color: #333;
}
.referral-alert {
  background-color: rgba(0, 123, 255, 0.06);
  border: 1px solid rgba(0, 123, 255, 0.12);
}
.share-card-preview {
  display: flex;
  background-color: #f8f9fa;
  padding: 12px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
}
.share-card-left {
  flex: 1;
  padding-right: 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.share-card-title {
  font-size: 0.9rem;
  font-weight: bold;
  color: #212529;
  margin-bottom: 4px;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.share-card-desc {
  font-size: 0.75rem;
  color: #6c757d;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.share-card-right {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}
.share-card-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}
.qr-section canvas {
  width: 160px !important;
  height: 160px !important;
}

/* 暗黑模式 */
body.theme-dark .share-modal-content {
  color: #f0f6fc;
}
body.theme-dark .referral-alert {
  background-color: rgba(56, 139, 253, 0.1);
  border-color: rgba(56, 139, 253, 0.2);
}
body.theme-dark .share-card-preview {
  background-color: #1f242c;
  border-color: #30363d;
}
body.theme-dark .share-card-title {
  color: #f0f6fc;
}
body.theme-dark .share-card-desc {
  color: #8b949e;
}
</style>
